import React, { useEffect } from "react";
import ReactCrop from "react-image-crop";

import ActionButton from "@root/Components/Buttons/ActionButton";
import StandardModal from "@root/Components/Modals/StandardModal";
import { useDebounceEffect } from '@root/Helpers/useDebounceEffect'
import { canvasPreview } from '@root/Helpers/ImageHelper';

const FileUploader = (props) => {
    const [ uploaderValue, setUploaderValue ] = React.useState('');
    const [ uploaderClasses, setUploaderClasses ] = React.useState();
    const [ files, setFiles ] = React.useState([]);
    const [ cropModalOpen, setCropModalOpen ] = React.useState(false);
    const [ crop, setCrop ] = React.useState(null);
    const [ cropImage, setCropImage ] = React.useState(null);
    const [ cropCompleted, setCropCompleted ] = React.useState(null);
    const [ cropButtonDisabled, setCropButtonDisabled ] = React.useState(true);

    const cropImageRef = React.createRef(null);
    const cropCanvasRef = React.createRef(null);

    useEffect(() => {
        setFiles(props.value);
    }, [props.value]);

    useDebounceEffect(
        async () => {
            if (
                cropCompleted?.width &&
                cropCompleted?.height &&
                cropImageRef.current &&
                cropCanvasRef.current
            ) {
                canvasPreview(
                    cropImageRef.current,
                    cropCanvasRef.current,
                    cropCompleted,
                )
                setCropButtonDisabled(false);
            }
        },
        100,
        [cropCompleted]
    )

    const handleDragOver = (e) => {
        e.preventDefault();

        setUploaderClasses('file-uploader-hover');
    }

    const handleDragLeave = (e) => {
        e.preventDefault();

        setUploaderClasses('');
    }

    const handleDrop = (e) => {
        e.preventDefault();

        var localFiles = []
        if (props.multiple) {
            localFiles = [...files];
        }

        if (e.dataTransfer.items) {
            Object.values(e.dataTransfer.items).map(item => {
                if (item.kind == 'file') {
                    localFiles.push(item.getAsFile());
                }
            });
        } else {
            Object.values(e.dataTransfer.files).map(item => {
                localFiles.push(item);
            });
        }

        localFiles = localFiles.map(file => {
            file.url = URL.createObjectURL(file);
            return file;
        });

        if (props.crop) {
            setCropImage(localFiles[0]);
            setCropModalOpen(true);
        } else {
            setFiles(localFiles);
            setUploaderClasses('');

            props.onChange(localFiles);
        }
    }

    const handleFileChange = (e) => {
        var localFiles = []
        if (props.multiple) {
            localFiles = [...files];
        }

        Object.values(e.target.files).map(file => {
            file.url = URL.createObjectURL(file);
            localFiles.push(file);
        });

        if (props.crop) {
            setCropImage(localFiles[0]);
            setCropModalOpen(true);
        } else {
            setFiles(localFiles);
            props.onChange(localFiles);
        }
    }

    const handleRemove = (file) => {
        let localFiles = files.filter(image => file.url != image.url);
        setFiles(localFiles);

        props.onChange(localFiles);
    }

    const saveCroppedImage = () => {
        var localFiles = [];
        if (props.multiple) {
            localFiles = [...files];
        }

        cropCanvasRef.current.toBlob((blob) => {
            let croppedFile = new File([blob], cropImage.name, { type: cropImage.type });
            croppedFile.url = URL.createObjectURL(blob);
            localFiles.push(croppedFile);

            setFiles(localFiles);
            props.onChange(localFiles);
            setCropModalOpen(false);

            setCrop(null);
            setCropImage(null);
            setCropCompleted(null);
            setCropButtonDisabled(true);
        }, cropImage.type, 1);
    }

    return (
        <div className="file-uploader-wrapper">
            <label
                htmlFor={props.name}
                className={`file-uploader ${uploaderClasses}`}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                {props.placeholder}

                <a className="file-uploader-button">{props.buttonText}</a>

                <input type="file"
                    id={props.name}
                    name={props.name}
                    onChange={handleFileChange}
                    value={uploaderValue}
                />
            </label>

            { props.message && <p className="file-uploader-message">{props.message}</p> }

            { files.length > 0 &&
                <div className="grid-x margin-bottom-1">
                    { files.map((file, index) => (
                        <div key={index}
                            className={`cell small-12 ${(props.type==='image') ? 'medium-6' : 'medium-12'} file-uploader-preview`}
                        >
                            {(props.type === 'image') && (
                                <img src={file.url}
                                    className="file-uploader-preview"
                                />
                            )}
                            <a href={file.url}
                               target="_blank"
                               className="file-uploader-preview-link"
                            >
                                {file.name}
                            </a>
                            <a
                                onClick={e => handleRemove(file)}
                                className="file-uploader-preview-remove"
                            >
                                Remove
                            </a>
                        </div>
                    ))}
                </div>
            }

            { props.crop && cropModalOpen &&
                <StandardModal
                    title="Crop Image"
                    open={cropModalOpen}
                    onClose={e => setCropModalOpen(false)}
                    buttons={[
                        <button
                            type="button"
                            className="button secondary"
                            onClick={e => setCropModalOpen(false)}
                        >
                            Cancel
                        </button>,
                        <ActionButton
                            type="button"
                            className="button primary"
                            isDisabled={cropButtonDisabled}
                            onClick={saveCroppedImage}
                            animateOnClick
                            title="Crop"
                        />
                    ]}
                >
                    <p style={{marginTop: "-0.5rem", marginBottom: "1.35rem"}}>
                        Click and drag cursor over image to make visible area selection.
                    </p>

                    <ReactCrop
                        crop={crop}
                        onChange={(crop, percentCrop) => setCrop(crop)}
                        onComplete={c => setCropCompleted(c)}
                        aspect={props.cropRatio}
                    >
                        <img
                            ref={cropImageRef}
                            src={cropImage.url}
                        />
                    </ReactCrop>
                    { cropCompleted &&
                        <canvas
                            ref={cropCanvasRef}
                            style={{
                                display: 'none',
                                objectFit: 'contain',
                                width: cropCompleted.width,
                                height: cropCompleted.height
                            }}
                        />
                    }
            </StandardModal>
            }
        </div>
    );
}

FileUploader.defaultProps = {
    value: [],
    name: 'file-uploader',
    placeholder: 'Choose file',
    buttonText: 'Browse',
    onChange: () => {},
    multiple: false,
    message: null,
    crop: false,
    cropRatio: null,
    type: 'image',
}

export default FileUploader;
